import React, { useState, useEffect, useCallback, useRef } from "react";
import * as THREE from 'three';
import useEditorStore from "../../../stores/useEditorStore";
import usePreparingScanStore from "../../../stores/usePreparingScanStore";
import { useShallow } from "zustand/shallow";
import { useFillHighlight } from "./FillToolHighlighter";

export function FillToolManager() {
    const { scene, camera, renderer, shadedMesh, controls } = useEditorStore(
        useShallow(s => ({
            scene: s.sceneRefs?.scene,
            camera: s.sceneRefs?.camera,
            renderer: s.sceneRefs?.renderer,
            shadedMesh: s.sceneRefs?.shadedMesh,
            controls: s.sceneRefs?.controls,
        }))
    );
    const setCursorOverride = useEditorStore(s => s.setCursorOverride);

    const [brushRadius] = useEditorStore(
        useShallow((state) => [
          state.toolSettings.scan_preparing_pencil?.size || 1,
        ])
      );

    const [selectedVertices, setSelectedVertices, clearFillSelection] = usePreparingScanStore(
        useShallow(
            (state) => [
                state.selectedFillVertices,
                state.setSelectedFillVertices,
                state.clearFillSelection
            ]
        )
    );

    const [isPainting, setIsPainting] = useState(false);
    // const brushRadius = 1;
    const OUTER_OFFSET = 0.2;

    const updateHighlights = useFillHighlight(shadedMesh, scene, renderer);
    const brushRef = useRef()

    // This allows the callbacks to access the latest state without needing to be re-created.
    const paintingStateRef = useRef({ isPainting, selectedVertices, shadedMesh, controls, camera, renderer, brushRadius, setSelectedVertices });

    // 1) create the ring once
    useEffect(() => {
        if (!scene) return
        // inner radius 1, outer 1.02 so it’s just an outline
        const geom = new THREE.RingGeometry(1, 1 + OUTER_OFFSET, 32)
        const mat = new THREE.MeshBasicMaterial({
        color: 0xffffff,
        side: THREE.DoubleSide,
        transparent: true,
        opacity: 0.7,
        depthTest: false,        // always on top
        })
        const ring = new THREE.Mesh(geom, mat)
        ring.visible = false
        scene.add(ring)
        ring.scale.set( brushRadius, brushRadius, brushRadius)
        brushRef.current = ring

        return () => {
        scene.remove(ring)
        geom.dispose()
        mat.dispose()
        }
    }, [scene])

    //Update ring size when brushRadius changes
    useEffect(()=>{
        const ring = brushRef.current;
        if(!ring) return;
        // compute new outer ratio so that (outerRatio * radius) - (1*radius) = OUTER_OFFSET
        const outerRatio = 1 + OUTER_OFFSET / brushRadius;
        // dump old geometry
        ring.geometry.dispose();
        // new unit ring from 1→outerRatio
        ring.geometry = new THREE.RingGeometry( 1, outerRatio, 32 );
        // now scale to get your actual radius
        ring.scale.set( brushRadius, brushRadius, brushRadius );
    }, [brushRadius]);
    
    // Keep the ref updated with the latest values on every render
    useEffect(() => {
        paintingStateRef.current = { isPainting, selectedVertices, shadedMesh, controls, camera, renderer, brushRadius, setSelectedVertices };
    });

    const handlePointerDown = useCallback((e) => {
        if(e.button !== 0 || !getRaycastHit()) return;
        setIsPainting(true);
        setCursorOverride('none');
    }, [setCursorOverride]);

    const handlePointerUp = useCallback(() => {
        // Access controls from the ref to keep this callback stable
        const { controls } = paintingStateRef.current;
        if (controls) controls.enableRotate = true;
        brushRef.current.visible = false;
        setIsPainting(false);
        setCursorOverride(null);
    }, [setCursorOverride]);

    const handlePointerMove = useCallback((event) => {
        // Read all state and props from the ref inside the handler
        const { isPainting, renderer, camera, shadedMesh, controls, selectedVertices, brushRadius, setSelectedVertices } = paintingStateRef.current;
        
        if (!isPainting || !renderer || !camera || !shadedMesh || !controls) return;

        const hit = getRaycastHit(event);
        if(!hit){
            brushRef.current.visible = false;
            return;
        }

        const ring = brushRef.current
        ring.position.copy(hit.point)
        ring.lookAt(camera.position)
        ring.scale.set(brushRadius, brushRadius, brushRadius)
        ring.visible = true
    
        controls.enableRotate = false;
        const hitPoint = hit.point;
        const newSelected = new Set(selectedVertices);
    
        const posAttr = shadedMesh.geometry.attributes.position;
        const normAttr = shadedMesh.geometry.attributes.normal;
        const worldMatrix = shadedMesh.matrixWorld;
        const vLocal = new THREE.Vector3(), vWorld = new THREE.Vector3();
        const nLocal = new THREE.Vector3(), nWorld = new THREE.Vector3();
        const viewDir = new THREE.Vector3();
    
        for (let i = 0; i < posAttr.count; i++) {
          vLocal.fromBufferAttribute(posAttr, i);
          vWorld.copy(vLocal).applyMatrix4(worldMatrix);
          if (vWorld.distanceTo(hitPoint) > brushRadius) continue;
    
          nLocal.fromBufferAttribute(normAttr, i);
          nWorld.copy(nLocal).transformDirection(worldMatrix);
    
          viewDir.copy(camera.position).sub(vWorld).normalize();
    
          if (nWorld.dot(viewDir) > 0) {
            newSelected.add(i);
          }
        }
        setSelectedVertices(newSelected);
      }, []);
    

    useEffect(() => {
        if (!renderer) return;
        const domElement = renderer.domElement;

        domElement.addEventListener('pointerdown', handlePointerDown);
        domElement.addEventListener('pointermove', handlePointerMove);
        document.addEventListener('pointerup', handlePointerUp);
        
        return () => {
          domElement.removeEventListener('pointerdown', handlePointerDown);
          domElement.removeEventListener('pointermove', handlePointerMove);
          document.removeEventListener('pointerup', handlePointerUp);
        };
    }, [renderer, handlePointerDown, handlePointerMove, handlePointerUp]);

    // This effect correctly calls the update function when the selection changes
    useEffect(() => {
        if (updateHighlights) {
            updateHighlights(selectedVertices);
        }
    }, [selectedVertices, updateHighlights]);

    // Effect for component cleanup
    useEffect(() => {
        return () => {
            clearFillSelection();
        }
    }, [clearFillSelection]);

    const getRaycastHit = function(){
        const domElement = renderer.domElement;
        const rect = domElement.getBoundingClientRect();
        const pointer = new THREE.Vector2(
          ((event.clientX - rect.left) / rect.width) * 2 - 1,
          -((event.clientY - rect.top) / rect.height) * 2 + 1
        );
    
        const raycaster = new THREE.Raycaster();
        raycaster.setFromCamera(pointer, camera);
        const hits = raycaster.intersectObject(shadedMesh);
        if (hits.length === 0){
            return null;
        }
        else
            return hits[0];
    }

    return null;
}
